import { Action } from '@ngrx/store';
import { IPackData, IPackItem, OrderStep } from '@app/certificates/packs/packs';
import { ICertificateData } from '@app/certificates/manage/manage';
import { ISpringResponseBody, PaymentStatusCode } from '@app/shared/interfaces/response-interfaces';
import { IOrder } from '@app/certificates/history/history';
import { ILegalPerson } from '@app/certificates/packs/invoice-form/invoice-form';
import { ICompanyDataAsPayer } from '@app/profile/main/details';

export const packKey = 'packs';

export enum PacksActionsTypes {
    // Очакващи резултат
    MAKE_ORDER = '[Packs] Make order',
    SHOW_ORDER = '[Packs] Show Order',
    GET_PAYMENT_DATA = '[Packs] Get Payment data',
    PAYMENT_INITIATED = '[Packs] Payment initiated',
    PAYMENT_RESTORED = '[Packs] Payment restored',

    // Резултат
    PAYMENT_UPDATED = '[Packs] Payment updated',
    MAKE_ORDER_SUCCESS = '[Packs] Make order success',
    PAYMENT_DATA_ERROR = '[Packs] Payment data error',
    PAYMENT_SUCCESS = '[Packs] Payment success',
    PAYMENT_FAILED = '[Packs] Payment failed',
    PAYMENT_ERROR = '[Packs] Payment error',
    ERROR = '[Packs] Error',

    // Други
    LIST = '[Packs] List Packs',
    RESET = '[Packs] Reset Packs',
    REVIEW = '[Packs] Review Pack',
}

/**
 * OrderStep.LIST (1)
 * Initializes available packages
 */
export class ActionPacksInitialize implements Action {
    readonly type = PacksActionsTypes.LIST;
    constructor(public payload: {items: IPackItem[]}) { }
}

/**
 * OrderStep.RESET (1)
 * Resets Packages page to show step1
 *
 * Note: Selected certificate is optional
 */
export class ActionPacksReset implements Action {
    readonly type = PacksActionsTypes.RESET;
    constructor() { }
}

/**
 * OrderStep.REVIEW (2)
 * Initializes selected pack review and invoice details form
 */
export class ActionPacksReviewSelection implements Action {
    readonly type = PacksActionsTypes.REVIEW;
    constructor(public payload: { packId: IPackItem['id'] }) { }
}

/**
 * OrderStep.REVIEW (2)
 * Initiates order creation
 */
export class ActionPacksMakeOrder implements Action {
    readonly type = PacksActionsTypes.MAKE_ORDER;
    constructor(public payload: {
        packId: IPackItem['id'],
        buyer?: ILegalPerson,
        origin: string,
        companyPayerData?: ICompanyDataAsPayer
    }) { }
}

/**
 * OrderStep.PAYMENT (3)
 * Order creation is successful, user goes to payment step.
 */
export class ActionPacksMakeOrderSuccess implements Action {
    readonly type = PacksActionsTypes.MAKE_ORDER_SUCCESS;
    constructor(public order: IOrder) { }
}

/**
 * OrderStep.PAYMENT (3)
 * When user clicks Pay button. System generates new payment gateway data
 */
 export class ActionPacksGetPaymentData implements Action {
    readonly type = PacksActionsTypes.GET_PAYMENT_DATA;
    constructor(public order: IOrder) { }
}

/**
 * OrderStep.PAYMENT (3)
 * Payment gateway link is received and system opens payment gateway window
 */
export class ActionPacksPaymentInitiated implements Action {
    readonly type = PacksActionsTypes.PAYMENT_INITIATED;
    constructor(public orderCode: string) { }
}

/**
 * OrderStep.PAYMENT (3)
 * Payment is restored from history page
 */
 export class ActionPacksRestoreOrderPayment implements Action {
    readonly type = PacksActionsTypes.PAYMENT_RESTORED;
    constructor(public order: IOrder) { }
}

/**
 * OrderStep.PAYMENT (3)
 * To update payment after getOrderStatus operation
 */
export class ActionPacksPaymentUpdated implements Action {
    readonly type = PacksActionsTypes.PAYMENT_UPDATED;
    constructor(public order: IOrder, public paymentStatus: PaymentStatusCode) { }
}

/**
 * OrderStep.PAYMENT (3)
 * When payment link there is an error when payment link is received
 */
 export class ActionPacksPaymentDataError implements Action {
    readonly type = PacksActionsTypes.PAYMENT_DATA_ERROR;
    constructor(public payload: { response?: ISpringResponseBody }) { }
}

/**
 * OrderStep.PAYMENT (3)
 * When payment is successful
 */
export class ActionPacksPaymentSuccess implements Action {
    readonly type = PacksActionsTypes.PAYMENT_SUCCESS;
    constructor(public order: IOrder) { }
}

/**
 * OrderStep.PAYMENT (3)
 * When payment is not successful
 */
export class ActionPacksPaymentFailed implements Action {
    readonly type = PacksActionsTypes.PAYMENT_FAILED;

    constructor(public order: IOrder) { }
}

/**
 * OrderStep.PAYMENT (3)
 * When there is an error with payment
 */
export class ActionPacksPaymentError implements Action {
    readonly type = PacksActionsTypes.PAYMENT_ERROR;
    constructor(public order: IOrder, public response?: ISpringResponseBody) { }
}

/**
 * Used to open initialize order details in packs from order history
 */
export class ActionPacksShowOrder implements Action {
    readonly type = PacksActionsTypes.SHOW_ORDER;
    constructor(public order: IOrder, public paymentStatus: PaymentStatusCode) { }
}

/**
 * OrderStep.REVIEW (2)
 * When there is an error when creating order
 */
export class ActionPacksError implements Action {
    readonly type = PacksActionsTypes.ERROR;
    constructor(public payload: { response?: ISpringResponseBody }) { }
}

export type PacksActions =
    ActionPacksInitialize |
    ActionPacksReset |
    ActionPacksReviewSelection |
    ActionPacksShowOrder |
    ActionPacksRestoreOrderPayment |
    ActionPacksMakeOrder |
    ActionPacksMakeOrderSuccess |
    ActionPacksGetPaymentData |
    ActionPacksPaymentInitiated |
    ActionPacksPaymentDataError |
    ActionPacksPaymentSuccess |
    ActionPacksPaymentFailed |
    ActionPacksPaymentError |
    ActionPacksPaymentUpdated |
    ActionPacksError;

export const initialState: IPackData = {
    items: null,
    step: OrderStep.LIST
    // wait: false
};
export const selectorPacks = state => (state.packs || initialState) as IPackData;

export function packsReducer(
    state: IPackData,
    action: PacksActions
): IPackData {
    switch (action.type) {
        case PacksActionsTypes.LIST:
            return {
                ...state,
                items: action.payload.items,
                step: OrderStep.LIST
            };
        case PacksActionsTypes.RESET:
            return {
                ...state,
                selectedPackId: null,
                paymentStatus: null,
                order: null,
                step: OrderStep.LIST
            };
        case PacksActionsTypes.REVIEW:
            return {
                ...state,
                step: OrderStep.REVIEW,
                selectedPackId: action.payload.packId
            };
        case PacksActionsTypes.MAKE_ORDER:
            return {
                ...state
            };
        case PacksActionsTypes.MAKE_ORDER_SUCCESS:
            return {
                ...state,
                order: action.order,
                step: OrderStep.PAYMENT,
                paymentStatus: PaymentStatusCode.NEW
            };
        case PacksActionsTypes.ERROR:
            return {
                ...state
            };
        case PacksActionsTypes.GET_PAYMENT_DATA:
            return {
                ...state,
                paymentStatus: PaymentStatusCode.NEW
            };
        case PacksActionsTypes.PAYMENT_INITIATED:
            return {
                ...state,
                paymentStatus: PaymentStatusCode.NEW
            };
        case PacksActionsTypes.PAYMENT_RESTORED:
            return {
                ...state,
                order: action.order,
                step: OrderStep.PAYMENT,
                paymentStatus: PaymentStatusCode.NEW
            };
        case PacksActionsTypes.PAYMENT_UPDATED:
            return {
                ...state,
                order: action.order,
                step: OrderStep.PAYMENT,
                paymentStatus: action.paymentStatus
            };
        case PacksActionsTypes.PAYMENT_SUCCESS:
            return {
                ...state,
                order: action.order,
                step: OrderStep.PAYMENT,
                paymentStatus: PaymentStatusCode.FINISHED_SUCCESS
            };
        case PacksActionsTypes.PAYMENT_FAILED:
            return {
                ...state,
                order: action.order,
                step: OrderStep.PAYMENT,
                paymentStatus: PaymentStatusCode.FINISHED_REJECTED
            };
        case PacksActionsTypes.SHOW_ORDER:
            return {
                ...state,
                order: action.order,
                paymentStatus: action.paymentStatus,
                step: OrderStep.PAYMENT
            };
        case PacksActionsTypes.PAYMENT_ERROR:
            return {
                ...state,
                order: action.order,
                paymentStatus: PaymentStatusCode.ERROR,
                step: OrderStep.PAYMENT
        };
        case PacksActionsTypes.PAYMENT_DATA_ERROR:
            return {
                ...state,
                paymentStatus: PaymentStatusCode.NEW
            };
        default:
            return state;
    }
}
